import { useState } from "react";
import { FileCheck2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const DEVIS_ORIGIN = process.env.REACT_APP_DEVIS_ORIGIN;
const params = new URLSearchParams(window.location.search);
export const RETOUR_DEVIS = params.get("retour") === "devis";
const prixParam = parseFloat(String(params.get("prix") || "").replace(",", "."));
export const DEVIS_PRIX = prixParam > 0 ? prixParam : null;
const montantParam = parseFloat(String(params.get("montant") ?? "").replace(",", "."));
export const DEVIS_SOLDE = montantParam >= 0 ? montantParam : null;
const eurDevis = (v) => `${v.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;

export function DevisPrixWarning({ prix, onRestore }) {
  if (DEVIS_PRIX == null || parseFloat(prix) === DEVIS_PRIX) return null;
  return (
    <p className="mt-2 text-xs sm:text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2" data-testid="devis-prix-warning">
      Le prix ne correspond plus à celui de votre devis ({DEVIS_PRIX.toLocaleString("fr-FR")} €).{" "}
      <button type="button" onClick={onRestore} data-testid="devis-prix-restore"
        className="font-semibold underline underline-offset-2 hover:text-orange-900">
        Rétablir le prix du devis
      </button>
    </p>
  );
}

// Pré-remplissage du formulaire de coordonnées depuis le configurateur.
export const DEVIS_LEAD = (() => {
  const g = (k) => (params.get(k) || "").trim();
  const out = {};
  if (["van", "remorque"].includes(g("gamme"))) out.projet = g("gamme") === "van" ? "van" : "fourgon";
  if (g("achat") === "immediat") out.projet_achat = "immediat";
  if (["oui", "non"].includes(g("reprise"))) out.van_reprendre = g("reprise");
  const ch = g("chevaux").replace(",", ".");
  if (["1", "1.5", "2", "3", "4", "5"].includes(ch)) out.nb_chevaux = ch;
  for (const [p, k] of [["nom", "nom"], ["prenom", "prenom"], ["societe", "societe"], ["email", "email"], ["tel", "tel"],
    ["adresse", "adresse"], ["cp", "code_postal"], ["ville", "ville"], ["pays", "pays"]]) if (g(p)) out[k] = g(p);
  const mr = parseFloat(g("montant_reprise").replace(",", "."));
  if (out.van_reprendre === "oui" && mr > 0) out.montant_reprise = mr;
  return out;
})();

export function devisAnswers(base) {
  const p = DEVIS_PRIX;
  if (!RETOUR_DEVIS && !(p > 0)) return null;
  const a = { ...base, depuis_devis: true, annee_naissance: "", resident_fr: true };
  if (DEVIS_LEAD.code_postal) a.code_postal = DEVIS_LEAD.code_postal;
  const etat = (params.get("etat") || "").trim().toLowerCase();
  if (etat === "neuf" || etat === "occasion") a.etat_vehicule = etat;
  if (etat === "occasion") {
    // Âge de l'occasion : annee=AAAA (prioritaire) ou age=N (années).
    const y = new Date().getFullYear();
    const an = parseInt(params.get("annee"), 10);
    const age = parseFloat(String(params.get("age") ?? "").replace(",", "."));
    const annee = an >= 1950 && an <= y ? an : age >= 0 && y - age >= 1950 ? Math.round(y - age) : null;
    if (annee != null) Object.assign(a, { annee_vehicule: String(annee), vehicule_moins_5ans: y - annee < 5 });
  }
  const num = (k) => { const v = parseFloat(String(params.get(k) ?? "").replace(",", ".")); return v >= 0 ? v : null; };
  const apP = num("apport"), mtP = num("montant");
  if (p > 0) {
    // Priorité : montant fourni (solde à la livraison → apport = prix − montant) > apport fourni > 20 % automatique.
    let ap = mtP != null ? p - mtP : apP != null ? apP : Math.round(p * 0.2);
    ap = Math.min(Math.max(ap, 0), p);
    Object.assign(a, { prix_remorque: String(p), apport_eur: String(ap), montant: String(p - ap), apport_touched: apP != null || mtP != null });
  } else if (mtP != null) {
    a.montant = String(mtP);
  }
  return a;
}

// TAEG (décimal) : taux actuariel annuel tel que net = Σ paiements actualisés (paiement k au mois k+1).
export function taegFrom(net, payments) {
  if (!(net > 0) || payments.length === 0) return null;
  const pv = (i) => payments.reduce((s, m, k) => s + m / Math.pow(1 + i, k + 1), 0);
  let lo = 0, hi = 1;
  if (pv(0) <= net) return 0;
  for (let k = 0; k < 100; k++) {
    const mid = (lo + hi) / 2;
    if (pv(mid) > net) lo = mid; else hi = mid;
  }
  return Math.round((Math.pow(1 + lo, 12) - 1) * 10000) / 10000;
}

export function DevisRecap({ answers, projet }) {
  const total = DEVIS_PRIX ?? parseFloat(answers.prix_remorque);
  const solde = DEVIS_SOLDE ?? parseFloat(answers.montant);
  const apport = total - solde;
  const etat = answers.etat_vehicule === "neuf" ? "Neuf"
    : answers.etat_vehicule === "occasion" ? `Occasion${answers.annee_vehicule ? ` (${answers.annee_vehicule})` : ""}` : null;
  const gamme = { van: "Van tracté", fourgon: "Remorque fourgon Pro&Sport" }[projet];
  const rows = [
    ["Total après remise", total > 0 && eurDevis(total), "total"],
    ["Apport", apport >= 0 && total > 0 && solde >= 0 && eurDevis(apport), "apport"],
    ["Solde à la livraison", solde >= 0 && eurDevis(solde), "solde"],
    ["Gamme", gamme, "gamme"],
    ["État", etat, "etat"],
  ].filter(([, v]) => v);
  return (
    <div className="rounded-2xl border border-[#e0d3ba] bg-[#faf7f1] p-5 mb-6" data-testid="devis-recap">
      <p className="flex items-center gap-2 text-sm font-bold text-[#9E7B41] uppercase tracking-wider">
        <FileCheck2 className="h-4 w-4" /> Votre devis
      </p>
      <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
        {rows.map(([k, v, id]) => (
          <div key={id}>
            <dt className="text-xs text-muted-foreground">{k}</dt>
            <dd className="text-sm font-semibold text-[#2b2b2b]" data-testid={`devis-recap-${id}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function DevisBanner() {
  const [noOpener, setNoOpener] = useState(false);
  const back = () => {
    if (!window.opener) { setNoOpener(true); return; }
    window.close();
  };
  return (
    <div className="bg-[#9E7B41] text-white" data-testid="devis-banner">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-2 flex items-center justify-between gap-3">
        <span className="flex flex-wrap items-center gap-x-2 text-sm font-semibold">
          <FileCheck2 className="h-4 w-4 shrink-0" /> Simulation liée à votre devis
          {(DEVIS_PRIX != null || DEVIS_SOLDE != null) && (
            <span className="basis-full text-xs sm:text-sm font-normal text-white/90" data-testid="devis-banner-amounts">
              {DEVIS_PRIX != null && <span className="whitespace-nowrap">Total après remise : <b className="font-semibold text-white">{eurDevis(DEVIS_PRIX)}</b></span>}
              {DEVIS_PRIX != null && DEVIS_SOLDE != null && " · "}
              {DEVIS_SOLDE != null && <span className="whitespace-nowrap">Solde à la livraison : <b className="font-semibold text-white">{eurDevis(DEVIS_SOLDE)}</b></span>}
            </span>
          )}
        </span>
        <button type="button" onClick={back} data-testid="devis-back-button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 text-sm font-semibold rounded-full bg-white/15 hover:bg-white/25 px-3 py-1 transition-colors">
          <ArrowLeft className="h-4 w-4" /> <span className="sm:hidden">Devis</span><span className="hidden sm:inline">Revenir au devis</span>
        </button>
      </div>
      {noOpener && (
        <p className="max-w-3xl mx-auto px-5 sm:px-8 pb-2 text-sm font-semibold text-white" data-testid="devis-back-error">
          Ouvrez le simulateur depuis le devis pour pouvoir y revenir.
        </p>
      )}
    </div>
  );
}

const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

export function ApplyDevisButton({ sol, solId, duree, mensualite, premier, taeg, montant, cout_total, prix_ttc }) {
  const [noOpener, setNoOpener] = useState(false);
  if (!RETOUR_DEVIS) return null;

  const apply = () => {
    if (!window.opener) { setNoOpener(true); return; }
    try {
    window.opener.postMessage({
      type: "fautras-financement",
      solution: sol.name,
      provider: sol.provider,
      duree,
      mensualite: r2(mensualite),
      premier_versement: premier != null && r2(premier) !== r2(mensualite) ? r2(premier) : null,
      taeg: taeg == null ? null : taeg,
      montant: r2(montant),
      cout_total: r2(cout_total),
      prix_ttc: (prix_ttc ?? sol.prix_ttc) ? r2(prix_ttc ?? sol.prix_ttc) : null,
      apport: r2(parseFloat(sol.apport) || 0),
    }, DEVIS_ORIGIN);
    } catch (err) {
      console.error("Envoi au devis impossible :", err);
      return;
    }
    // Fermeture uniquement après l'envoi effectif du message.
    window.close();
  };

  return (
    <div className="mt-5">
      <Button type="button" onClick={apply} data-testid={`apply-devis-${solId}`}
        className="w-full h-12 rounded-xl bg-[#9E7B41] hover:bg-[#876634] text-white font-semibold">
        <FileCheck2 className="h-4 w-4 mr-2" /> Appliquer au devis
      </Button>
      {noOpener && (
        <p className="mt-2 text-sm text-red-300" data-testid={`apply-devis-error-${solId}`}>
          Ouvrez le simulateur depuis le devis pour pouvoir y appliquer ce financement.
        </p>
      )}
    </div>
  );
}
